import { db } from '@/lib/db/database'
import { strapiGet, strapiPost, strapiPut, strapiPatch, strapiDelete, StrapiError } from '@/lib/strapi'
import { eventBus } from '@/lib/eventBus/events'

// Re-export so existing importers keep working
export type { HttpMethod, QueuedOp } from '@/lib/db/database'

const MAX_RETRIES = 3
let activeDrain: Promise<ProcessResult> | null = null

export interface ProcessResult {
  succeeded: number
  failed: number
}

/**
 * Substitute any tempId references in a string with the resolved real documentId.
 */
function resolveStr(s: string, resolvedIds: Record<string, string>): string {
  let resolved = s
  for (const [tempId, realId] of Object.entries(resolvedIds)) {
    resolved = resolved.replace(tempId, realId)
  }
  return resolved
}

/**
 * Recursively walk a request body and substitute any tempId strings with the
 * resolved real documentId. This handles relations like `booking: 'temp-123'`
 * that reference an entity created earlier in the same sync pass.
 */
function resolveBody(body: unknown, resolvedIds: Record<string, string>): unknown {
  if (typeof body === 'string') return resolveStr(body, resolvedIds)
  if (Array.isArray(body)) return body.map((item) => resolveBody(item, resolvedIds))
  if (body !== null && typeof body === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(body as Record<string, unknown>)) {
      out[k] = resolveBody(v, resolvedIds)
    }
    return out
  }
  return body
}

export const queueManager = {
  async enqueue(
    op: Omit<import('@/lib/db/database').QueuedOp, 'id' | 'createdAt' | 'retries'>,
  ): Promise<void> {
    await db.syncQueue.add({
      ...op,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      retries: 0,
    })
  },

  async processAll(): Promise<ProcessResult> {
    if (activeDrain) return activeDrain
    activeDrain = this.processAllUnlocked()
    try {
      return await activeDrain
    } finally {
      activeDrain = null
    }
  },

  async processAllUnlocked(): Promise<ProcessResult> {
    const queue = await db.syncQueue.orderBy('createdAt').toArray()
    if (queue.length === 0) return { succeeded: 0, failed: 0 }

    eventBus.emit('sync:started', { queueLength: queue.length })

    let succeeded = 0
    let failed = 0
    const toDelete: string[] = []
    // Accumulates tempId→realId mappings as POSTs succeed, for path substitution
    const resolvedIds: Record<string, string> = {}

    for (const op of queue) {
      const resolvedPath = resolveStr(op.path, resolvedIds)
      const resolvedBody = resolveBody(op.body, resolvedIds)

      try {
        let result: { data?: { documentId?: string } } | undefined

        if (op.method === 'POST')        result = await strapiPost(resolvedPath, resolvedBody)
        else if (op.method === 'PUT')    result = await strapiPut(resolvedPath, resolvedBody)
        else if (op.method === 'PATCH')  result = await strapiPatch(resolvedPath, resolvedBody)
        else if (op.method === 'DELETE') await strapiDelete(resolvedPath)

        succeeded++
        toDelete.push(op.id)

        if (op.tempId && op.method === 'POST') {
          const responseData = result?.data as any
          const serverDocumentId = responseData?.documentId ?? responseData?.payment?.documentId ?? ''
          resolvedIds[op.tempId] = serverDocumentId

          if (op.path === '/api/bookings') {
            // Bookings use the eventBus so useBookingStore can react
            eventBus.emit('booking:synced', { tempId: op.tempId, serverDocumentId })
          } else if (op.path === '/api/booking-pre-order-items') {
            // Replace temp pre-order item in Dexie with the real record
            const temp = await db.preOrderItems.get(op.tempId)
            if (temp && serverDocumentId) {
              await db.preOrderItems.delete(op.tempId)
              await db.preOrderItems.put({ ...temp, documentId: serverDocumentId })
            }
          } else if (op.path === '/api/pos/orders' || /^\/api\/pos\/bookings\/.+\/ensure-order$/.test(op.path)) {
            // Replace temp POS order with the real one fetched from server
            const tempOrder = await db.posOrders.get(op.tempId)
            if (tempOrder && serverDocumentId) {
              await db.posOrders.delete(op.tempId)
              try {
                const fullResp = await strapiGet(`/api/pos/orders/${serverDocumentId}`, {
                  populate: {
                    table: true,
                    orderItems: { populate: { menuItem: true, variant: true } },
                    payments: true,
                    booking: true,
                  },
                })
                const realOrder = (fullResp as any)?.data ?? fullResp
                if (realOrder?.documentId) {
                  await db.posOrders.put({
                    ...realOrder,
                    tableDocId: realOrder.table?.documentId ?? '',
                    orderItems: [
                      ...(realOrder.orderItems ?? []),
                      ...(tempOrder.orderItems ?? []).filter((item) => item.documentId.startsWith('temp-')),
                    ],
                    payments: [
                      ...(realOrder.payments ?? []),
                      ...(tempOrder.payments ?? []).filter((payment) => payment.documentId.startsWith('temp-')),
                    ],
                  })
                }
              } catch {
                // Offline during sync cleanup — preserve temp data with real ID
                await db.posOrders.put({ ...tempOrder, documentId: serverDocumentId })
              }
            }
          } else if (op.path === '/api/pos-order-items') {
            // Replace temp item inside its parent POS order
            const allOrders = await db.posOrders.toArray()
            const parentOrder = allOrders.find((o) => o.orderItems?.some((i) => i.documentId === op.tempId))
            if (parentOrder && serverDocumentId) {
              const serverItem = (result as any)?.data
              const newItems = parentOrder.orderItems.map((i) => {
                if (i.documentId !== op.tempId) return i
                // Preserve menuItem/variant from temp (server may not populate them)
                if (serverItem) return { ...i, ...serverItem, menuItem: serverItem.menuItem ?? i.menuItem, variant: serverItem.variant ?? i.variant }
                return { ...i, documentId: serverDocumentId }
              })
              await db.posOrders.update(parentOrder.documentId, { orderItems: newItems })
            }
          } else if (op.path === '/api/pos/payments') {
            // Replace temp payment inside its parent POS order
            const body = resolvedBody as any
            const orderDocId = body?.orderDocumentId
            if (orderDocId) {
              const order = await db.posOrders.get(orderDocId)
              if (order && serverDocumentId) {
                const serverPay = (result as any)?.data?.payment ?? (result as any)?.data
                const newPayments = order.payments.map((p) => {
                  if (p.documentId !== op.tempId) return p
                  return serverPay ? { ...p, ...serverPay } : { ...p, documentId: serverDocumentId }
                })
                await db.posOrders.update(orderDocId, { payments: newPayments })
              }
            }
          }
        }
      } catch (err) {
        const isClientError = err instanceof StrapiError && err.status < 500
        const exhausted = op.retries + 1 >= MAX_RETRIES

        if (isClientError) {
          failed++
          toDelete.push(op.id)
          if (op.tempId) {
            if (op.path === '/api/bookings') {
              eventBus.emit('booking:sync-failed', {
                tempId: op.tempId,
                error: err instanceof Error ? err.message : 'Unknown error',
              })
            } else if (op.path === '/api/booking-pre-order-items') {
              await db.preOrderItems.delete(op.tempId)
            } else if (op.path === '/api/pos/orders' || /^\/api\/pos\/bookings\/.+\/ensure-order$/.test(op.path)) {
              await db.posOrders.delete(op.tempId)
            } else if (op.path === '/api/pos-order-items') {
              // Remove temp item from parent order
              const allOrders = await db.posOrders.toArray()
              const parentOrder = allOrders.find((o) => o.orderItems?.some((i) => i.documentId === op.tempId))
              if (parentOrder) {
                await db.posOrders.update(parentOrder.documentId, {
                  orderItems: parentOrder.orderItems.filter((i) => i.documentId !== op.tempId),
                })
              }
            } else if (op.path === '/api/pos/payments') {
              // Remove temp payment from parent order
              const body = resolvedBody as any
              const orderDocId = body?.orderDocumentId
              if (orderDocId) {
                const order = await db.posOrders.get(orderDocId)
                if (order) {
                  await db.posOrders.update(orderDocId, {
                    payments: order.payments.filter((p) => p.documentId !== op.tempId),
                  })
                }
              }
            }
          }
        } else {
          // Server/network failures must never silently discard a sale.
          await db.syncQueue.update(op.id, { retries: exhausted ? MAX_RETRIES : op.retries + 1 })
        }
      }
    }

    await db.syncQueue.bulkDelete(toDelete)
    eventBus.emit('sync:completed', { succeeded, failed })
    return { succeeded, failed }
  },

  async getQueueLength(): Promise<number> {
    return db.syncQueue.count()
  },
}
